import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    memberId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    renewalDue: '',
    modeOfPayment: 'Cash',
    status: 'Paid',
    isTrialMember: false,
    trialMemberName: '',
    trialMemberPhone: '',
    trialMemberEmail: ''
  });
  
  // Search functionality state
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMemberName, setSelectedMemberName] = useState('');
  const dropdownRef = useRef(null);
  
  // Payment list search state
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Cache for member search (name + phone for quick lookup)
  const [memberCache, setMemberCache] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async (memberId = null) => {
    try {
      setIsSearching(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch payments - limited initially, or by member if searching
      const paymentsUrl = memberId 
        ? `/api/payments?memberId=${memberId}`
        : '/api/payments?limit=10';
      
      const [paymentsRes, membersRes] = await Promise.all([
        axios.get(paymentsUrl, { headers }),
        axios.get('/api/members?limit=100', { headers })
      ]);

      setPayments(paymentsRes.data.payments || []);
      setMembers(membersRes.data.members || []);
      
      // Build member cache for search suggestions
      const cache = (membersRes.data.members || []).map(m => ({
        id: m.id,
        name: m.name,
        phone: m.phone
      }));
      setMemberCache(cache);
      
      if (memberId) setHasSearched(true);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Search payments by member
  const handlePaymentSearch = () => {
    if (paymentSearchTerm.trim()) {
      // Find member by name or phone
      const member = memberCache.find(m => 
        m.name?.toLowerCase().includes(paymentSearchTerm.toLowerCase()) ||
        m.phone?.includes(paymentSearchTerm)
      );
      if (member) {
        fetchPaymentsByMember(member.id);
      } else {
        alert('No member found with that name/phone');
      }
    }
  };

  // Fetch payments for a specific member
  const fetchPaymentsByMember = async (memberId) => {
    try {
      setIsSearching(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/payments?memberId=${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.payments || []);
      setHasSearched(true);
    } catch (error) {
      console.error('Failed to search payments:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Enter key in search
  const handlePaymentSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePaymentSearch();
    }
  };

  // Clear search and show recent
  const clearPaymentSearch = () => {
    setPaymentSearchTerm('');
    setHasSearched(false);
    fetchData();
  };

  // Get search suggestions from cache
  const paymentSearchSuggestions = paymentSearchTerm.length >= 2 
    ? memberCache.filter(m => 
        m.name?.toLowerCase().includes(paymentSearchTerm.toLowerCase()) ||
        m.phone?.includes(paymentSearchTerm)
      ).slice(0, 5)
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      if (editingPayment) {
        await axios.put(`/api/payments/${editingPayment.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Payment updated successfully!');
      } else {
        await axios.post('/api/payments', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccessMessage('Payment recorded successfully!');
        
        // If trial member checkbox is checked, create a lead
        if (formData.isTrialMember && formData.trialMemberName) {
          try {
            await axios.post('/api/leads', {
              name: formData.trialMemberName,
              phone: formData.trialMemberPhone,
              email: formData.trialMemberEmail,
              date: formData.paymentDate,
              source: 'Trial Member',
              status: 'New',
              response: `Trial payment of ₹${formData.amount}`
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (leadError) {
            console.error('Failed to create lead from trial member:', leadError);
          }
        }
      }
      fetchData();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to record payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Payment deleted successfully!');
      fetchData();
    } catch (error) {
      alert('Failed to delete payment');
    }
  };

  const openEditModal = (payment) => {
    setEditingPayment(payment);
    const member = members.find(m => m.id === payment.memberId);
    setFormData({
      memberId: payment.memberId,
      amount: payment.amount,
      paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
      renewalDue: payment.dueDate ? new Date(payment.dueDate).toISOString().split('T')[0] : '',
      modeOfPayment: payment.modeOfPayment || 'Cash',
      status: payment.status || 'Paid'
    });
    setSelectedMemberName(member ? `${member.name} - ${member.phone}` : payment.memberName);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPayment(null);
    setFormData({
      memberId: '',
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      renewalDue: '',
      modeOfPayment: 'Cash',
      status: 'Paid',
      isTrialMember: false,
      trialMemberName: '',
      trialMemberPhone: '',
      trialMemberEmail: ''
    });
    setSearchTerm('');
    setSelectedMemberName('');
    setShowDropdown(false);
  };

  // Filter members based on search term
  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm)
  );

  const handleMemberSelect = (member) => {
    // Calculate default renewal due: 1 month from payment date
    const paymentDateObj = new Date(formData.paymentDate);
    const defaultRenewalDue = new Date(paymentDateObj);
    defaultRenewalDue.setMonth(defaultRenewalDue.getMonth() + 1);
    
    setFormData({ 
      ...formData, 
      memberId: member.id,
      renewalDue: defaultRenewalDue.toISOString().split('T')[0]
    });
    setSelectedMemberName(`${member.name} - ${member.phone}`);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Loading payments...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Payments</h1>
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          + Record Payment
        </button>
      </div>

      {/* Success Message Popup */}
      {successMessage && (
        <div className="success-popup" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#4CAF50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s'
        }}>
          {successMessage}
          <button 
            onClick={() => setSuccessMessage('')}
            style={{
              marginLeft: '15px',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >×</button>
        </div>
      )}

      {/* Payment Search */}
      <div className="search-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search payments by member name or phone..."
            value={paymentSearchTerm}
            onChange={(e) => setPaymentSearchTerm(e.target.value)}
            onKeyPress={handlePaymentSearchKeyPress}
            style={{
              width: '300px',
              padding: '10px 15px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              outline: 'none'
            }}
          />
          {/* Search suggestions dropdown */}
          {paymentSearchSuggestions.length > 0 && paymentSearchTerm.length >= 2 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '5px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              zIndex: 100,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {paymentSearchSuggestions.map(s => (
                <div
                  key={s.id}
                  onClick={() => { setPaymentSearchTerm(s.name); fetchPaymentsByMember(s.id); }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  <strong>{s.name}</strong> - {s.phone}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={handlePaymentSearch}
          disabled={isSearching || !paymentSearchTerm.trim()}
          style={{
            padding: '10px 20px',
            background: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: paymentSearchTerm.trim() ? 'pointer' : 'not-allowed',
            opacity: paymentSearchTerm.trim() ? 1 : 0.6
          }}
        >
          {isSearching ? '🔄' : '🔍'} Search
        </button>
        {hasSearched && (
          <button
            onClick={clearPaymentSearch}
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ✕ Clear
          </button>
        )}
        <span style={{ color: '#666', fontSize: '13px' }}>
          {hasSearched 
            ? `Found ${payments.length} payment${payments.length !== 1 ? 's' : ''}`
            : `Showing last 10 payments`}
        </span>
      </div>

      <div className="table-container">
        {payments.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Amount</th>
                <th>Payment Mode</th>
                <th>Payment Date</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.memberName}</td>
                  <td>₹{payment.amount}</td>
                  <td>{payment.modeOfPayment || 'Cash'}</td>
                  <td>{formatDate(payment.paymentDate)}</td>
                  <td>{formatDate(payment.dueDate)}</td>
                  <td>
                    <span className={`status-badge status-${payment.status?.toLowerCase()}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" onClick={() => openEditModal(payment)}>
                        Edit
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(payment.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <h3>No payments recorded</h3>
            <p>Record a payment to track member transactions</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingPayment ? 'Edit Payment' : 'Record Payment'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group" ref={dropdownRef}>
                <label>Member</label>
                <div className="search-select">
                  <input
                    type="text"
                    value={selectedMemberName || searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value);
                      setSelectedMemberName('');
                      setFormData({ ...formData, memberId: '' });
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search member by name or phone..."
                    required={!formData.memberId}
                  />
                  {showDropdown && (
                    <div className="search-dropdown">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map(member => (
                          <div
                            key={member.id}
                            className="search-dropdown-item"
                            onClick={() => handleMemberSelect(member)}
                          >
                            <strong>{member.name}</strong> - {member.phone}
                          </div>
                        ))
                      ) : (
                        <div className="search-dropdown-item no-results">
                          No members found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <input type="hidden" value={formData.memberId} required />
              </div>
              <div className="form-group">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={e => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mode of Payment</label>
                <select
                  value={formData.modeOfPayment}
                  onChange={e => setFormData({ ...formData, modeOfPayment: e.target.value })}
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={e => {
                    const newPaymentDate = e.target.value;
                    // Auto-update renewal due to 1 month from new payment date
                    const newRenewalDue = new Date(newPaymentDate);
                    newRenewalDue.setMonth(newRenewalDue.getMonth() + 1);
                    setFormData({ 
                      ...formData, 
                      paymentDate: newPaymentDate,
                      renewalDue: newRenewalDue.toISOString().split('T')[0]
                    });
                  }}
                  required
                />
              </div>
              <div className="form-group">
                <label>Renewal Due Date</label>
                <input
                  type="date"
                  value={formData.renewalDue}
                  onChange={e => setFormData({ ...formData, renewalDue: e.target.value })}
                  required
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  This will update the member's renewal due date
                </small>
              </div>
              
              {/* Trial Member Section - Only show when adding new payment */}
              {!editingPayment && (
                <div className="form-group" style={{ 
                  background: formData.isTrialMember ? '#e8f5e9' : '#f5f5f5', 
                  padding: '15px', 
                  borderRadius: '8px',
                  marginTop: '10px',
                  transition: 'background 0.3s'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    marginBottom: formData.isTrialMember ? '15px' : '0'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.isTrialMember}
                      onChange={e => setFormData({ ...formData, isTrialMember: e.target.checked })}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>🎯 Trial Member (Add to Leads)</span>
                  </label>
                  
                  {formData.isTrialMember && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div className="form-group" style={{ marginBottom: '5px' }}>
                        <label style={{ fontSize: '12px' }}>Lead Name *</label>
                        <input
                          type="text"
                          value={formData.trialMemberName}
                          onChange={e => setFormData({ ...formData, trialMemberName: e.target.value })}
                          placeholder="Trial member name"
                          required={formData.isTrialMember}
                          style={{ padding: '8px' }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: '5px' }}>
                        <label style={{ fontSize: '12px' }}>Phone</label>
                        <input
                          type="tel"
                          value={formData.trialMemberPhone}
                          onChange={e => setFormData({ ...formData, trialMemberPhone: e.target.value })}
                          placeholder="Phone number"
                          style={{ padding: '8px' }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: '5px', gridColumn: 'span 2' }}>
                        <label style={{ fontSize: '12px' }}>Email</label>
                        <input
                          type="email"
                          value={formData.trialMemberEmail}
                          onChange={e => setFormData({ ...formData, trialMemberEmail: e.target.value })}
                          placeholder="Email address"
                          style={{ padding: '8px' }}
                        />
                      </div>
                      <small style={{ gridColumn: 'span 2', color: '#666', fontSize: '11px' }}>
                        This will automatically create a lead entry with source "Trial Member Payment"
                      </small>
                    </div>
                  )}
                </div>
              )}
              
              {editingPayment && (
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Paid">Paid</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : (editingPayment ? 'Update Payment' : 'Record Payment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
